import { ReactNode } from 'react';
import {
    Box,
    Container,
    CssBaseline,
    Stack,
    ThemeProvider,
    Typography,
} from '@mui/material';
import { netScotlandTheme } from '../theme/netScotlandTheme';

/**
 * Public-facing layout shell for `/calendar` and `/events/:id` (D-12).
 *
 * IMPORTANT: This file MUST NOT import from the admin framework or from
 * the auth-provider module. The whole point of mounting public routes
 * ABOVE the `<Admin>` tree is so they do not trigger the auth-check
 * callback (Pitfall 5 in 01-RESEARCH.md). Even transitive admin-side
 * imports can pull a context dependency that does the auth check.
 *
 * Layout follows `01-UI-SPEC.md` §"Public surfaces":
 *   - Max content width 1120px, centred (theme.md §4 fixed dimension)
 *   - No left rail, no top app bar
 *   - `surface-canvas` background (#F7F9FC)
 *   - Header lockup centred — `<img src="/net-scotland-logo.png" />`
 *   - Footer with single-line `netscotland.org`, no internal nav links
 *
 * Plan 13: replaces the Plan 08 local stub theme with the canonical
 * `netScotlandTheme` (single source of truth for palette + typography +
 * component overrides). The same theme is also mounted at the app root
 * in `src/index.tsx`; this inner ThemeProvider preserves the public
 * surface's framework-isolation guarantee if a future refactor moves
 * the root provider.
 */
type PublicLayoutProps = {
    children: ReactNode;
};

export const PublicLayout = ({ children }: PublicLayoutProps) => (
    <ThemeProvider theme={netScotlandTheme}>
        <CssBaseline />
        <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
            <Container
                maxWidth={false}
                sx={{ maxWidth: '1120px', mx: 'auto', px: 3, pt: 8 }}
            >
                <Stack spacing={6}>
                    <Box component="header" textAlign="center">
                        <Box
                            component="img"
                            src="/net-scotland-logo.png"
                            alt="NET Scotland"
                            sx={{
                                display: 'inline-block',
                                maxHeight: 80,
                                height: 'auto',
                                width: 'auto',
                            }}
                        />
                    </Box>
                    <Box>{children}</Box>
                    <Box component="footer" textAlign="center" pt={4} pb={6}>
                        <Typography variant="body2" color="text.secondary">
                            <a
                                href="https://netscotland.org"
                                style={{
                                    color: 'inherit',
                                    textDecoration: 'none',
                                }}
                            >
                                netscotland.org
                            </a>
                        </Typography>
                    </Box>
                </Stack>
            </Container>
        </Box>
    </ThemeProvider>
);

export default PublicLayout;
